import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useDarkModeColors } from '@atlassian/color-picker';

import { useProductDependencies } from '../../controllers/use-product-dependencies';
import { ButtonStyle, CommonLayout } from '../../types';
import { validateAndSanitizeUrl } from '../../utils/url-validation';
import { VERTICAL_MAX_WIDTH } from '../spotlight-layout';
import i18n from './messages';

const SPOTLIGHT_BREAKPOINTS = {
	small: 640,
	medium: 1400,
};

const styles = cssMap({
	baseWrapperStyles: { height: '100%' },
	smallStyles: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	mediumStyles: {
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
	largeStyles: {
		paddingTop: token('space.800'),
		paddingRight: token('space.800'),
		paddingBottom: token('space.800'),
		paddingLeft: token('space.800'),
	},
	largeTitle: { font: token('font.heading.large') },
	xlargeTitle: { font: token('font.heading.xlarge') },
	xxlargeTitle: { font: token('font.heading.xxlarge') },
	titleStyles: { fontWeight: token('font.weight.semibold') },
	smallFrameStyles: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	mediumFrameStyles: {
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.400'),
		paddingRight: token('space.400'),
	},
	largeFrameStyles: {
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

export const getWrapperStyle = (
	width: number,
	layout: CommonLayout,
):
	| 'smallFrameStyles'
	| 'mediumFrameStyles'
	| 'largeFrameStyles'
	| 'smallStyles'
	| 'mediumStyles'
	| 'largeStyles' => {
	if (layout === CommonLayout.FRAME_ABOVE_IMAGE) {
		if (width < SPOTLIGHT_BREAKPOINTS.small) {
			return 'smallFrameStyles';
		}
		if (width < SPOTLIGHT_BREAKPOINTS.medium) {
			return 'mediumFrameStyles';
		}
		return 'largeFrameStyles';
	}

	if (width < SPOTLIGHT_BREAKPOINTS.small) {
		return 'smallStyles';
	}
	if (width < SPOTLIGHT_BREAKPOINTS.medium) {
		return 'mediumStyles';
	}
	return 'largeStyles';
};

const baseButtonStyles = xcss({
	borderWidth: 'border.width',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "5px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '5px'),
	transition: 'background 0.1s ease-out',
	borderStyle: 'solid',
	borderColor: 'color.border',
	paddingBlock: 'space.100',
	paddingInline: 'space.150',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'none',
	},
});

const defaultButtonStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	':hover': {
		backgroundColor: 'elevation.surface.overlay.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.overlay.pressed',
	},
});

const alternativeButtonStyles = xcss({
	backgroundColor: 'color.background.neutral.bold',
	':hover': {
		backgroundColor: 'color.background.neutral.bold.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.bold.pressed',
	},
});

const getTitleFontSize = (width: number) => {
	if (width < SPOTLIGHT_BREAKPOINTS.small) {
		return 'largeTitle';
	}
	if (width < SPOTLIGHT_BREAKPOINTS.medium) {
		return 'xlargeTitle';
	}
	return 'xxlargeTitle';
};

const getDescriptionSize = (width: number) => {
	if (width < SPOTLIGHT_BREAKPOINTS.small) {
		return 'medium';
	}
	return 'large';
};

const getBaseLineCount = (isSplitLayout: boolean, width: number) => {
	if (!isSplitLayout) {
		return 8;
	}
	if (width < SPOTLIGHT_BREAKPOINTS.small) {
		return 3;
	}
	return 6;
};

const getDescriptionMaxLines = (isSplitLayout: boolean, width: number, isButtonShown: boolean) => {
	const lines = getBaseLineCount(isSplitLayout, width);
	return isButtonShown ? lines - 2 : lines;
};

export type SpotlightContentProps = {
	buttonStyle?: ButtonStyle;
	buttonText?: string;
	description?: string;
	imageWidthPercentage?: number;
	isButtonShown: boolean;
	layout: CommonLayout;
	link: string;
	source?: string;
	testId?: string;
	textColor: string;
	title?: string;
	width?: number;
};

export const SpotlightContent = ({
	testId,
	width = VERTICAL_MAX_WIDTH + 1,
	layout,
	textColor: savedTextColor,
	title,
	description,
	isButtonShown,
	buttonText,
	link,
	buttonStyle,
	source,
	imageWidthPercentage = 0.5,
}: SpotlightContentProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSSRProductInDarkMode } = useProductDependencies();

	const isSplitLayout =
		layout !== CommonLayout.NO_IMAGE_BACKGROUND && layout !== CommonLayout.FRAME_ABOVE_IMAGE;
	const isDefaultButtonStyle = buttonStyle === ButtonStyle.DEFAULT;

	const { getDarkModeCompatibleTextColor } = useDarkModeColors(isSSRProductInDarkMode);
	const textColor = getDarkModeCompatibleTextColor(savedTextColor);

	const handleButtonClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'spotlightButton',
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const bodyTextMaxLines = useMemo(
		() => getDescriptionMaxLines(isSplitLayout, width, isButtonShown),
		[isSplitLayout, width, isButtonShown],
	);

	const titleMaxLines = () => {
		if (!description) {
			return 5;
		}
		if (imageWidthPercentage > 0.5 && width < SPOTLIGHT_BREAKPOINTS.small) {
			return 2;
		}
		return 3;
	};
	const safeUrl = validateAndSanitizeUrl(link);

	return (
		<Stack
			grow="fill"
			space={width < SPOTLIGHT_BREAKPOINTS.small ? 'space.150' : 'space.200'}
			alignInline="start"
			alignBlock="center"
			xcss={cx(styles.baseWrapperStyles, styles[getWrapperStyle(width, layout)])}
			testId={testId}
		>
			{/* Title */}
			{title && (
				<Text align="start" color="inherit" maxLines={titleMaxLines()} testId="spotlight-title">
					<Box
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ color: textColor }}
						xcss={cx(styles[getTitleFontSize(width)], styles.titleStyles)}
					>
						{title}
					</Box>
				</Text>
			)}

			{/* Description */}
			{description &&
				bodyTextMaxLines > 0 && (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					<span style={{ color: textColor }}>
						<Text
							testId="spotlight-description"
							align="start"
							color="inherit"
							size={getDescriptionSize(width)}
							maxLines={bodyTextMaxLines}
						>
							{description}
						</Text>
					</span>
				)}

			{/* Button */}
			{isButtonShown && safeUrl && (
				<Anchor
					onClick={handleButtonClick}
					href={safeUrl}
					xcss={[
						baseButtonStyles,
						isDefaultButtonStyle ? defaultButtonStyles : alternativeButtonStyles,
					]}
				>
					<Text
						weight="medium"
						color={isDefaultButtonStyle ? 'color.text' : 'color.text.inverse'}
						maxLines={1}
					>
						{buttonText || <FormattedMessage {...i18n.placeholderButtonLabel} />}
					</Text>
				</Anchor>
			)}
		</Stack>
	);
};
